import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  AggregateScoreType,
  GetAggregateScoresDocument,
  OrderDirection,
  ScoreRating,
  useGetAggregateScoresQuery,
} from "~/operations";

import { useSort } from "~/pages/inventory/hooks/useSort";
import { usePolicyOutletContext } from "./policy-gql";
import { Fragment, useEffect } from "react";
import { FirewatchList } from "../FirewatchList";
import { mapSelectedEntitiesToString } from "../exceptions/utils/mapSelectedEntitiesToString";
import { useExceptions } from "../exceptions/use-exceptions";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { useSearch } from "../search/useSearch";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import { ScopeType } from "~/hooks/useScope";
import { useParams, useSearchParams } from "react-router-dom";
import { usePaginate } from "../pagination/usePaginate";
import { useDialogs } from "@toolpad/core";
import {
  SelectedEntityWithFindingData,
  SetExceptionDialog,
} from "~/components/exceptions/SetExceptionDialog";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { useSelection } from "~/hooks/useSelection";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { SelectedEntityWithTicketData } from "~/pages/tickets/types";
import { SelectedEntityWithAggregateScoresData } from "~/components/FirewatchPage";

export function PolicyChecks() {
  const { policy, policyEnabled, space, scope, exceptionGroups } =
    usePolicyOutletContext();
  const { policyId } = useParams();
  const [searchParams] = useSearchParams();
  const paginate = usePaginate();
  const baseUrl = paginate.resetCursorUrl(location.href);
  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();

  const search = useSearch({ baseUrl });
  const sort = useSort<AggregateScoreOrder>({
    baseUrl,
    defaultSort: {
      field: AggregateScoreOrderField.Rank,
      direction: OrderDirection.Asc,
    },
    validFields: [
      AggregateScoreOrderField.Rank,
      AggregateScoreOrderField.Title,
      AggregateScoreOrderField.BlastRadius,
      AggregateScoreOrderField.RiskValue,
    ],
  });

  const { data, loading } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scope.mrn,
      ...paginate.pageVars,
      orderBy: sort.orderBy,
      filter: {
        scoreType: AggregateScoreType.Check,
        policyMrn: policy.mrn,
        queryTerms: search.searchFilters,
      },
    },
    fetchPolicy: "cache-and-network",
  });

  const hasApplyExceptionPermission = useCanApplyExceptionMutation();

  const {
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    selectedEntities: selectedChecks,
    setSelectedEntities: setSelectedChecks,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<
    SelectedEntityWithFindingData &
      SelectedEntityWithException &
      SelectedEntityWithTicketData &
      SelectedEntityWithAggregateScoresData
  >();

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectedChecks,
    target: "check",
    showExceptionButtons: true,
    showScopeButtons: false,
  });

  useEffect(() => {
    if (selectedChecks.length > 0) {
      triageToolbar.show({
        buttons: [
          ...(exceptionsButtonsVisibility.SET_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
        ],
        payload: {
          count: selectedChecks.length,
          totalCount: edges.length,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedChecks]);

  const { handleRemoveExceptionGroups, handleSetException } = useExceptions({
    onSetException: () => {
      setSelectedChecks([]);
    },
    scopeMrns: [space.mrn],
    queryMrns: mapSelectedEntitiesToString(selectedChecks),
    refetchQueries: [GetAggregateScoresDocument],
  });

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedChecks([]);
      },
      exceptionMrns: exceptionGroups.map((eg) => eg.mrn),
    });
  };

  const handleSetExceptionModalOpen = () => {
    dialogs.open(SetExceptionDialog, {
      target: "check",
      onSave: handleSetException,
      shouldListEntities: true,
      selectedEntities: selectedChecks,
      targetData: {
        targetTitle: policy.name,
      },
    });
  };

  const aggregateScores =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  const pageInfo = aggregateScores?.pageInfo;
  const edges = aggregateScores?.edges || [];
  const paginateProps = paginate.pageProps(pageInfo);

  const hasEditPermission = hasApplyExceptionPermission && policyEnabled;

  const paginatedTargets = edges?.map((e) => ({
    ...e.node,
    groupId: e?.node?.exception?.id,
    rating: e?.node?.rating || ScoreRating.Unscored,
    title: e?.node?.title || "",
    mrn: e?.node?.findingMrn || "",
    exception: e?.node?.exception,
    data: {
      id: e?.node?.findingMrn || "",
      title: e?.node?.title || "",
      rating: e?.node?.rating || "",
    },
    scopeMrn: scope.mrn,
    isAffected: (e?.node?.blastRadius?.affected || 0) > 0,
    scoreType: AggregateScoreType.Check,
  }));

  return (
    <Fragment>
      <FilterBar searchId={""} placeholder={""} search={search} />
      <FirewatchList
        space={space}
        scope={scope}
        loading={loading}
        data={edges}
        pageType={AggregateScoreType.Check}
        sort={sort}
        paginateProps={paginateProps}
        selectionProps={{
          canSelect:
            hasApplyExceptionPermission &&
            hasEditPermission &&
            scope.type === ScopeType.Space,
          selectedEntities: selectedChecks,
          isGroupChecked: isGroupChecked(paginatedTargets),
          isGroupIndeterminate: isGroupIndeterminate(paginatedTargets),
          onAllCheckboxClick: (event) => {
            onGroupCheckChange(event, paginatedTargets);
          },
          onRowCheckboxChange: handleNodeChange,
          onRowCheckboxClick: handleNodeClick,
        }}
        noTags
        queryParams={{
          policyId: encodeURIComponent(policyId || ""),
          queryTerms: searchParams.get("queryTerms") || "",
        }}
      />
    </Fragment>
  );
}
